/**
 * @file Merges the form version's restore point ladder with one ladder per table
 * into the single recovery timeline the sidebar draws (SYD-2450). Consumer:
 * `useFormRecoveryList`, after its fan-out lands and again after a refinement
 * splices rows into the version ladder. Pure — no store, no callable — because it
 * is the piece most likely to be wrong and the easiest to test on its own.
 *
 * A version restore point's cursor is a commit time and a table rung's is a read
 * time, so the two never line up. The whole minute is the grain both already share
 * beyond the exact-time margin, so rows falling in one minute become one moment
 * carrying the NEWEST cursor among them — a moment every source collapsed there can
 * answer for.
 *
 * Every source's live head is that source's present, so all of them are ONE moment —
 * the record as it stands now — and the minute rule cannot reach that fact. The heads
 * carry unrelated cursors: the version's is the live document's commit time, which may
 * be hours old, while each table's is its own callable's `Date.now()` in micros, so the
 * heads of tables fanned out in parallel are equal or milliseconds apart and never line
 * up with the version's. Left alone those heads draw one row each, all meaning the
 * present, and the table rows are stamped with the moment the panel was opened rather
 * than with any change.
 *
 * `points[0]` of each source list IS that source's live head — the wire types say so
 * (`ListFormVersionRestorePointsResponse.points`, `ListArTableRestorePointsResponse.points`)
 * — and this merge relies on the index for nothing else. A refinement only ever splices
 * strictly after the row it was asked about, so index 0 survives a re-merge.
 *
 * Live entries sort ahead of every other row rather than only collapsing where they
 * happen to land adjacent: a table rung sampled a minute ago outranks a version head
 * committed three hours ago, and the grouping pass walks the list once. Sorting them
 * together is what makes "two live entries are one moment" hold at all.
 *
 * It cannot break the strict decrease. Each source list is itself strictly decreasing
 * with its head at index 0, so every other row of source S is strictly below head(S);
 * the live moment carries the maximum over all the heads, which is at least head(S) and
 * therefore strictly above every row S contributes. Comparing a head against another
 * source's rungs proves nothing and is not needed — a version head committed at 10:02
 * really does sit below a table rung read at 13:04.
 *
 * The minute is only that grain where both rows are stated in it. Inside the exact-time
 * hour a cursor addresses a microsecond, and four saves in one minute are four states
 * the operator may read and restore — collapsing them would keep the newest and make
 * the other three unreachable while the gap they lie in reports itself complete. So a
 * row joins the open moment only when that moment's head and the row are BOTH at minute
 * grain, or when the two name the very same cursor: two sources reporting one instant
 * are one moment, and `moments` is strictly decreasing by `cursorMicros`, which two
 * rows sharing a cursor would break.
 *
 * Ordering and grouping run on the row's display millis rather than on its cursor,
 * and the two are the same quantity: every source states its moment twice, once as
 * `cursorMicros` and once as the millis it renders at, and the second is the first
 * divided down. Working in millis keeps the minute arithmetic in the units the
 * minute is named in.
 *
 * `rowCountDelta` is never consulted. An open probe may replace the row-count half
 * of the table fingerprint, so it is a display number and never a reason to show or
 * hide a moment.
 */

import type { ArTableId, ArTableRestorePoint, FormRestorePoint, FormRestorePointsEnd } from '#types';

import type {
  RecoveryMoment,
  RecoveryMomentTable,
  RecoveryTableList,
} from '@/store/form/formRecoveryStore';

const MILLIS_PER_MINUTE = 60_000;

/** The version's own ladder, as `listFormVersionRestorePoints` answered it. */
export type RecoveryVersionList = {
  points: readonly FormRestorePoint[];
  end: FormRestorePointsEnd;
  refusedMomentCount: number;
};

export type MergeRecoveryMomentsInput = {
  /**
   * The version ladder. Always present: its refusal is fatal upstream, because a
   * recovery panel with no form history is nothing the tables can stand in for.
   */
  version: RecoveryVersionList;
  /** One entry per table whose ladder landed, in layout order. */
  tables: readonly RecoveryTableList[];
  /**
   * Tables whose ladder never landed. Each adds one to `refusedMomentCount`, because
   * moments that table would have reported are missing from the timeline; none
   * narrows `end`, because a list that did not land says nothing about where history
   * stops.
   */
  refusedTableCount: number;
};

export type MergedRecoveryMoments = {
  /** Strictly decreasing by `cursorMicros`. */
  moments: RecoveryMoment[];
  end: FormRestorePointsEnd;
  refusedMomentCount: number;
};

/**
 * One source's row before grouping. `rank` orders the sources against each other
 * when two of them report the same moment: the version first, then the tables in
 * layout order, so the collapse is deterministic and a table's entries keep the
 * order the layout gave them.
 *
 * `isLive` marks the source's own live head, which is `index` 0 of its list and
 * nothing else — both response types state that, and it is the only thing either
 * index is read for here.
 */
type MomentEntry = {
  cursorMicros: number;
  atMillis: number;
  minuteGrain: boolean;
  isLive: boolean;
  rank: number;
  version: FormRestorePoint | null;
  table: RecoveryMomentTable | null;
};

const minuteOf = (atMillis: number): number => Math.floor(atMillis / MILLIS_PER_MINUTE);

const versionEntry = (point: FormRestorePoint, index: number): MomentEntry => ({
  cursorMicros: point.cursorMicros,
  atMillis: point.committedAtMillis,
  minuteGrain: point.minuteGrain,
  isLive: index === 0,
  rank: 0,
  version: point,
  table: null,
});

const tableEntry = (
  tableId: ArTableId,
  point: ArTableRestorePoint,
  index: number,
  rank: number,
): MomentEntry => ({
  cursorMicros: point.cursorMicros,
  atMillis: point.readAtMillis,
  minuteGrain: point.minuteGrain,
  isLive: index === 0,
  rank,
  version: null,
  table: { tableId, point },
});

/**
 * The tables of one moment, at most one entry each and in layout order. Two rungs
 * of one table inside a moment keep the newer, which is the one the moment's own
 * cursor is closest to.
 */
const tablesOf = (entries: readonly MomentEntry[]): RecoveryMomentTable[] => {
  const seen = new Set<ArTableId>();
  return entries
    .filter((entry): entry is MomentEntry & { table: RecoveryMomentTable } => entry.table !== null)
    .filter((entry) => {
      if (seen.has(entry.table.tableId)) return false;
      seen.add(entry.table.tableId);
      return true;
    })
    .sort((left, right) => left.rank - right.rank)
    .map((entry) => entry.table);
};

/**
 * The entries of one moment, newest first, as one row. The head entry is the newest
 * of them — and, among equal moments, the lowest rank — so the moment's cursor,
 * stamp and grain all come from the same source row rather than from three.
 */
const momentOf = (entries: readonly MomentEntry[]): RecoveryMoment => {
  const [head] = entries;
  return {
    cursorMicros: head.cursorMicros,
    atMillis: head.atMillis,
    minuteGrain: head.minuteGrain,
    isLive: head.isLive,
    version: entries.find((entry) => entry.version !== null)?.version ?? null,
    tables: tablesOf(entries),
  };
};

/**
 * Whether `entry` belongs to the moment `head` opened. Two live heads are one moment
 * whatever their cursors or grain say: each states its own source's present, and the
 * present is a single instant across all of them. Otherwise the minute is the grain
 * only where both rows are stated in it — an exact-time row addresses a microsecond and
 * is its own moment — and a row naming the head's own cursor is the head's moment
 * whatever either says about grain.
 */
const collapses = (head: MomentEntry, entry: MomentEntry): boolean =>
  (head.isLive && entry.isLive) ||
  entry.cursorMicros === head.cursorMicros ||
  (head.minuteGrain && entry.minuteGrain && minuteOf(head.atMillis) === minuteOf(entry.atMillis));

/** `'floor'` beats `'creation'`: any source stopping at the retention floor stops the timeline. */
const narrowestEnd = (ends: readonly FormRestorePointsEnd[]): FormRestorePointsEnd =>
  ends.includes('floor') ? 'floor' : 'creation';

export const mergeRecoveryMoments = ({
  version,
  tables,
  refusedTableCount,
}: MergeRecoveryMomentsInput): MergedRecoveryMoments => {
  const entries = [
    ...version.points.map((point, index) => versionEntry(point, index)),
    ...tables.flatMap((list, rank) =>
      list.points.map((point, index) => tableEntry(list.tableId, point, index, rank + 1)),
    ),
  ].sort(
    /**
     * `isLive` leads, and it is `index === 0` taken on the source's word, so the
     * strictly-decreasing result is now conditional on each source list arriving
     * strictly decreasing — a list handed over out of order would put its head below
     * its own rows and the merge would return an ascending pair. Both callables
     * guarantee the order, and the refinement splice preserves it.
     */
    (left, right) =>
      Number(right.isLive) - Number(left.isLive) ||
      right.atMillis - left.atMillis ||
      right.cursorMicros - left.cursorMicros ||
      left.rank - right.rank,
  );

  const grouped = entries.reduce<MomentEntry[][]>((groups, entry) => {
    const head = groups[groups.length - 1]?.[0];
    if (head !== undefined && collapses(head, entry)) {
      const current = groups[groups.length - 1];
      return [...groups.slice(0, -1), [...current, entry]];
    }
    return [...groups, [entry]];
  }, []);

  return {
    moments: grouped.map(momentOf),
    end: narrowestEnd([version.end, ...tables.map((list) => list.end)]),
    refusedMomentCount:
      version.refusedMomentCount +
      tables.reduce((total, list) => total + list.refusedMomentCount, 0) +
      refusedTableCount,
  };
};
